import React, { memo, useState, useEffect, ChangeEvent } from 'react'
import { Avatar } from 'transfix-ui/components/Avatar'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { PhoneInput } from 'transfix-ui/components/PhoneInput'
import { Switch } from 'transfix-ui/components/Switch'
import { IDriver } from 'apps/fleet-planner/types'
import { cellPhoneSchema } from 'apps/fleet-planner/utils/cellphoneValidator'
import { formatPhone } from 'utils/formatPhone'
import { DriverOptedOutMessage } from './DriverOptedOutMessage'

interface IDriverConfirmationModalRowProps {
  driver?: IDriver | null
  driverIndex: number
  formError?: string
  onSetErrors: (errorMessage: string, driverIndex: number) => void
  onSetCellPhone: (cellPhone: string, driverIndex: number) => void
  onSetUpdatesToggle: (isToggled: boolean, driverIndex: number) => void
}

export const DriverSettingsModalRow = memo<IDriverConfirmationModalRowProps>(
  ({
    driver = {},
    onSetErrors,
    driverIndex,
    formError = '',
    onSetCellPhone,
    onSetUpdatesToggle,
  }) => {
    const { cellPhoneIsTwilioSubscribed = true } = driver || {}
    const { cellPhone: cellPhoneProp = '', settings } = driver || {}
    const cellPhone = formatPhone(cellPhoneProp, {
      removeCountryCode: '+1',
    })
    const { smsStatusUpdatesEnabled: smsStatusUpdatesEnabledProp = false } = settings || {}
    const [smsStatusUpdatesEnabled, setSmsStatusUpdatesEnabled] = useState<boolean>(
      smsStatusUpdatesEnabledProp
    )

    const validateCellPhone = async (cellPhoneNumber: string) => {
      try {
        await cellPhoneSchema.validate(cellPhoneNumber)
        onSetErrors('', driverIndex)
      } catch (e) {
        onSetErrors(e.message, driverIndex)
      }
    }

    // Anytime a user inputs a new cellphone number, validate it
    useEffect(() => {
      if (cellPhone) {
        validateCellPhone(cellPhone)
      }
    }, [cellPhone])

    // When the status opt-in is changed outside of this component, update it here as well
    useEffect(() => {
      if (smsStatusUpdatesEnabledProp) {
        setSmsStatusUpdatesEnabled(smsStatusUpdatesEnabledProp)
      }
    }, [smsStatusUpdatesEnabledProp])

    const driverUpdatesToggleHandler = () => {
      if (driverIndex === undefined) return
      setSmsStatusUpdatesEnabled(prev => !prev)
      onSetUpdatesToggle(!smsStatusUpdatesEnabled, driverIndex)
    }

    const driverCellPhoneChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
      if (driverIndex === undefined) return
      onSetCellPhone(e.target.value, driverIndex)
      validateCellPhone(cellPhone)
    }

    return (
      <div className='driver-settings-modal-row'>
        <div className='flex items-center mb4 mt4'>
          <div className='mr2'>
            <Avatar size='xsmall' key={driver?.name} name={driver?.name} />
          </div>
          <div className='b'>{driver?.name}</div>
        </div>
        <div className='flex mb2'>
          {/** NOTE: Label is left as '' because we don't want
           * the users to be able to toggle by clicking on the label */}
          <Switch
            label=''
            onChange={driverUpdatesToggleHandler}
            checked={smsStatusUpdatesEnabled}
            disabled={!cellPhoneIsTwilioSubscribed}
          />
          <span>Ask driver for status updates via text message</span>
        </div>
        {!cellPhoneIsTwilioSubscribed && smsStatusUpdatesEnabled && (
          <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={DriverOptedOutMessage()} />
        )}
        {smsStatusUpdatesEnabled && (
          <div className='pl0'>
            <PhoneInput
              clearable={false}
              required
              invalid={!!formError}
              validationMessage={formError}
              label='Cellphone'
              onChange={driverCellPhoneChangeHandler}
              placeholder='(000) 000 - 0000'
              value={cellPhone}
              className='fs-mask'
              large
            />
          </div>
        )}
      </div>
    )
  }
)
